import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { TaskList } from "@/components/TaskList";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { Icon } from "@/components/Icon";

/**
 * S5 Task list — designs/TaskList.html (project-scoped variant).
 * The project breadcrumb, header and tab strip live in the parent layout
 * route (`route.tsx`); this content-only view delegates to <TaskList />
 * with `showProjectColumn={false}` since every row belongs to the same
 * project.
 */
function ListView() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  // Shared search: `?q=` survives a reload, is shareable, and carries to the
  // Board view on switch (and vice-versa).
  const { q } = Route.useSearch();
  const [search, setSearch] = useState(q ?? "");
  const updateSearch = (v: string) => {
    setSearch(v);
    navigate({
      to: "/projects/$projectId/list",
      params: { projectId },
      search: { q: v || undefined },
      replace: true,
    });
  };
  const [teamFilter, setTeamFilter] = useState("");
  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const teamQ = useQuery({
    queryKey: queryKeys.team(),
    queryFn: api.listTeam,
  });
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });

  const userNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, m.name ?? m.email ?? m.id.slice(0, 8));
    }
    return map;
  }, [teamQ.data]);

  const userImages = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, m.image ?? null);
    }
    return map;
  }, [teamQ.data]);

  if (tasksQ.isLoading) {
    return (
      <div className="proj-shell-content">
        <Skeleton count={8} height={44} />
      </div>
    );
  }

  if (tasksQ.isError) {
    return (
      <div className="proj-shell-content">
        <EmptyState
          icon="close"
          title="Couldn't load this project."
          caption="Refresh the page or return to the project list."
        />
      </div>
    );
  }

  const allTasks = tasksQ.data?.tasks ?? [];
  const teamMemberIds = teamFilter
    ? new Set(
        (
          teamsQ.data?.teams.find((t) => t.id === teamFilter)?.members ?? []
        ).map((m) => m.userId),
      )
    : null;
  const queryLc = search.trim().toLowerCase();
  const tasks = allTasks.filter((t) => {
    if (queryLc && !t.title.toLowerCase().includes(queryLc)) return false;
    if (teamMemberIds) {
      const ids = t.assigneeIds?.length
        ? t.assigneeIds
        : t.assigneeId
          ? [t.assigneeId]
          : [];
      if (!ids.some((id) => teamMemberIds.has(id))) return false;
    }
    return true;
  });

  return (
    <div className="proj-shell-content">
      <div className="board-bar" style={{ marginBottom: 10 }}>
        <SegmentedToggle
          options={[
            { value: "board", label: "Board", icon: "board" },
            { value: "list", label: "List", icon: "clip" },
            { value: "table", label: "Table", icon: "grid" },
            { value: "calendar", label: "Calendar", icon: "cal" },
            { value: "timeline", label: "Timeline", icon: "arrow" },
            { value: "gantt", label: "Gantt", icon: "chev" },
          ]}
          value="list"
          onChange={(v) =>
            navigate({
              to:
                v === "board"
                  ? "/projects/$projectId"
                  : v === "table"
                    ? "/projects/$projectId/table"
                    : v === "calendar"
                      ? "/projects/$projectId/calendar"
                      : v === "timeline"
                        ? "/projects/$projectId/timeline"
                        : v === "gantt"
                          ? "/projects/$projectId/gantt"
                          : "/projects/$projectId/list",
              params: { projectId },
              search: v === "board" ? { q: search || undefined } : undefined,
            })
          }
        />
        <div className="input-search" style={{ width: 220 }}>
          <Icon name="search" size={14} />
          <input
            type="search"
            className="input"
            placeholder="Search tasks…"
            aria-label="Search tasks"
            value={search}
            onChange={(e) => updateSearch(e.target.value)}
          />
        </div>
        {(teamsQ.data?.teams ?? []).length > 0 && (
          <select
            className="select"
            style={{ width: 160, height: 32 }}
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            aria-label="Filter by team"
          >
            <option value="">All teams</option>
            {(teamsQ.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {allTasks.length === 0 ? (
        <EmptyState
          icon="check"
          title="No tasks yet."
          caption="Add a task from the Board view to get started."
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon="search"
          title="No matching tasks."
          caption={`Nothing matches “${search.trim()}”. Clear the search to see all tasks.`}
        />
      ) : (
        <TaskList
          tasks={tasks}
          userNames={userNames}
          userImages={userImages}
          showProjectColumn={false}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/list")({
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
  }),
  component: ListView,
});
